import { useEffect } from 'react';

import loadable from '@loadable/component';
import {
  BpkBox,
  BpkSpacing,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';

import { logExperimentChokepoint, logMiniEvent } from '../../logMiniEvent';
import withErrorBoundary from '../ErrorBoundary';
import { useMBDContext } from '../common/MBD/useMBDContext';

import DealsList from './DealsList/DealsList';
import FooterBlurb from './FooterBlurb/FooterBlurb';
import PriceBandSubtitle from './PriceBandSubtitle';
import getDealsCommonTrackingProps from './getDealsCommonTrackingProps';

import type { DealsEvolvedProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/DealsEvolved';

import STYLES from './DealsEvolved.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const DealsEvolved = (props: DealsEvolvedProps) => {
  useEffect(() => {
    if (
      props.trackingContext.pageType === 'flights-home' ||
      props.trackingContext.pageType === 'last-minute-deals'
    ) {
      logExperimentChokepoint('users_who_saw_destination_images_with_coverage');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { buildMBDParam, rawComponentName } = useMBDContext();
  const mbdComponentName = rawComponentName;
  const {
    airline,
    countryViewUrl,
    deals,
    dealsLimit,
    isMobile = false,
    pageTypeOptions,
    priceBandStrings,
    priceBandSubtitle,
    priceBandsDisplayState,
    shouldShow3by2Formation,
    strings,
    trackingContext,
  } = props;
  return (
    <div
      className={STYLES.DealsEvolved}
      id="deals-evolved"
      data-testid="dealsEvolved"
      data-tracking-element-id={mbdComponentName}
      data-tracking-common-params={buildMBDParam(mbdComponentName, {
        number_of_deals_requested: dealsLimit.toString(),
        number_of_deals_returned: deals.length.toString(),
        price_bands_display_state: priceBandsDisplayState,
      })}
    >
      <BpkBox marginBottom={BpkSpacing.LG}>
        <BpkSectionHeader
          title={String(strings.header)}
          description={strings.subheader}
        />
      </BpkBox>
      {priceBandSubtitle && (
        <PriceBandSubtitle priceBandSubtitle={priceBandSubtitle} />
      )}
      <DealsList
        {...{
          strings,
          deals,
          airline,
          originCode: trackingContext.originCode,
          destinationCode: trackingContext.destinationCode,
          isMobile,
          trackingContext,
          pageTypeOptions,
          priceBandStrings,
          priceBandsDisplayState,
          shouldShow3by2Formation,
          countryViewUrl,
          dealsLimit,
        }}
      />
      <FooterBlurb disclaimer={strings.disclaimer} />
    </div>
  );
};
const DealsEvolvedWithErrorBoundary = withErrorBoundary(
  DealsEvolved,
  'deals-evolved-component',
);
export default (props: DealsEvolvedProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEvent('DealsEvolvedEvent', 'deals_evolved_component', {
        deals_common: getDealsCommonTrackingProps(props),
        event_type: 'COMPONENT_SEEN',
      })
    }
  >
    <DealsEvolvedWithErrorBoundary {...props} />
  </IntersectionObserverWrapper>
);
